/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreHandler — error handling and pure utility functions.
 *
 * Provides:
 *   - ICoreUtils:       interface for injectable error handler
 *   - DefaultCoreUtils: server-safe implementation (logs to console, no UI)
 *   - SetCoreUtils():   replaces the handler at runtime (called by kalmstromShared4
 *                       to register ks, or by FunctionErrorHandler in Azure Functions)
 *   - Utility functions matching the ks.Nz / ks.Nb / ks.N0 / ... helpers
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

// ── Error-handler + product-version registry ─────────────────────────────────
// Lives in kCoreErrorRegistry.ts since 2026-10-06 (a small bundle can import it without this file's large tables). Re-exported here under the same
// names, so every existing `import { getCoreUtils, SetCoreUtils, ... } from "./kCoreHandler"` is unchanged and there is ONE singleton.
import { getCoreUtils } from "./kCoreErrorRegistry";
import { kCoreLocaleDateFormat } from "./kCoreLocaleTableCore";
import { kCoreHandlerReport } from "./kCoreHandlerReport";
import { kCoreHandlerLocale } from "./kCoreHandlerLocale";
import { kCoreHandlerText } from "./kCoreHandlerText";
import { kCoreHandlerUtil } from "./kCoreHandlerUtil";
import { kNotNullOrUndefined } from "./kCoreHandlerValues";
export type { ICoreUtils } from "./kCoreErrorRegistry";
export { SetCoreUtils, SetCoreUtilsResolver, getCoreUtils, setProductVersion, getProductVersion } from "./kCoreErrorRegistry";

// ── Stack-trace location parsing (error-handler support) ──────────────────────
// Pure, self-contained helper that extracts a source file name + line number
// from a V8 / browser Error stack, so an error report can name WHERE the error
// was raised. The Azure Functions handler (kFunctionErrorHandler) previously sent
// a blank ErrorFileName and no line — unlike the browser handler, which parses
// e.stack — so a server-side report identified only the function label. This
// lives in the shared core so both hosts route through one parser.
//
// ERROR-SUPPRESSED-JUSTIFIED: pure, deterministic, side-effect-free helper called
// FROM inside the error handler itself, so it must NEVER re-enter
// KGlobalErrorHandler (that would recurse). It therefore omits the KErrorMode
// guard and, on any malformed stack, swallows internally and returns the safe
// default { FileName: "", LineNumber: 0 } — the same "can't parse → blank"
// contract the browser handler's inline parser already uses.

/** File name (basename) + 1-based line number extracted from one Error stack. */
export interface IStackLocation {
    FileName: string;
    LineNumber: number;
}

/** Strip a stack frame's path down to its file basename (handles / and \). */
const StackBaseName = (pathText: string): string => {
    const NoDir: string = String(pathText).replace(/^.*[\\/]/, "");
    return NoDir.trim();
};

/**
 * Parse a single V8 / browser stack line into { FileName, LineNumber }, or null
 * when the line carries no `file:line:col` location (a message line, an
 * `<anonymous>` frame, or a `node:internal/...` runtime frame). Handles both the
 * parenthesised `at fn (path:line:col)` form and the bare `at path:line:col`
 * form, and Windows drive-letter paths (the leading `C:` colon is kept intact —
 * the line:col is matched only at the end of the frame).
 */
const ParseStackFrame = (line: string): IStackLocation | null => {
    const Match: RegExpExecArray | null = /\(?([^()\s][^()]*):(\d+):(\d+)\)?\s*$/.exec(line);
    if (Match === null) { return null; }
    if (Match[1].indexOf("node:") !== -1) { return null; }
    const LineNumber: number = parseInt(Match[2], 10);
    if (isNaN(LineNumber)) { return null; }
    const FileName: string = StackBaseName(Match[1]);
    if (FileName === "") { return null; }
    return { FileName: FileName, LineNumber: LineNumber };
};

/** True when a frame belongs to the error-handler infrastructure itself. Skipped
    so a wrapper Error constructed inside a handler reports the real caller, not
    the handler. Matches the `.ts` source and the compiled `.js` sibling. */
const IsHandlerFrame = (fileName: string): boolean => {
    if (fileName.indexOf("kCoreHandler") === 0) { return true; }
    if (fileName.indexOf("kFunctionErrorHandler") === 0) { return true; }
    return false;
};

/**
 * Extract the first meaningful source location from an Error stack. Walks the
 * frames top-down, skips the error-handler's own frames and Node runtime frames,
 * and returns the first frame carrying a `file:line:col`. Works for compiled
 * `.js` frames (a Node product that ships compiled output with no runtime
 * source-map support) as well as `.ts` frames (a product whose entry point turns
 * source maps on — see LangateSynch's `bundle-entry.js`, where the whole app is a
 * single esbuild bundle and the map is what keeps this function meaningful: with
 * maps OFF every frame is `app.js`, so `IsHandlerFrame` below stops matching and
 * the handler reports ITSELF instead of the caller). Returns
 * { FileName: "", LineNumber: 0 } when the stack is absent or unparseable — it
 * never throws (it is called from inside the error handler).
 */
export const parseStackLocation = (stack: string | undefined | null): IStackLocation => {
    const Empty: IStackLocation = { FileName: "", LineNumber: 0 };
    if (stack === undefined || stack === null || stack === "") { return Empty; }
    try {
        const Lines: string[] = String(stack).split("\n");
        for (const Line of Lines) {
            const Frame: IStackLocation | null = ParseStackFrame(Line);
            if (Frame === null) { continue; }
            if (IsHandlerFrame(Frame.FileName) === true) { continue; }
            return Frame;
        }
    } catch {
        return Empty;
    }
    return Empty;
};

/**
 * Reduce an Error stack to only its meaningful application frames: drop the leading
 * "Error: <message>" line(s) — the message is reported separately (ErrorDescription),
 * so repeating it in the stack is duplication — plus the error-handler's own frames
 * and Node runtime frames (no signal). Reuses the SAME ParseStackFrame + IsHandlerFrame
 * predicates as parseStackLocation, so the frame classification never drifts. Returns
 * "" when no application frame survives (the caller then falls back to the raw stack).
 * Never throws — it is called from inside the error handler (the parseStackLocation
 * carve-out): pure, no KErrorMode guard, safe-default on any malformed stack.
 */
export const formatStackFrames = (stack: string | undefined | null): string => {
    if (stack === undefined || stack === null || stack === "") { return ""; }
    try {
        const Kept: string[] = [];
        for (const Line of String(stack).split("\n")) {
            const Frame: IStackLocation | null = ParseStackFrame(Line);
            if (Frame === null) { continue; }
            if (IsHandlerFrame(Frame.FileName) === true) { continue; }
            Kept.push(Line.trim());
        }
        return Kept.join("\n");
    } catch {
        return "";
    }
};

/** The function name from a V8 frame — `at Foo.Bar (path:1:2)` → `Foo.Bar`. "" for the bare
    `at path:1:2` form, which carries no name. */
const StackMethodName = (line: string): string => {
    const Match: RegExpExecArray | null = /^\s*at\s+(.+?)\s+\(/.exec(line);
    if (Match === null) { return ""; }
    return Match[1].trim();
};

/** One stack frame, structured. Field names match Application Insights' `parsedStack` contract
    exactly, so a caller can hand the array straight to the ingestion endpoint. */
export interface IStackFrame {
    level: number;
    method: string;
    fileName: string;
    line: number;
}

/**
 * The STRUCTURED form of `formatStackFrames`: the same meaningful application frames, but split into
 * method / file / line instead of text.
 *
 * It exists because Application Insights groups exceptions by `problemId`, which it derives from the
 * top frame of `parsedStack` — a stack sent as one string groups everything under a single useless
 * bucket. Deliberately reuses the SAME `ParseStackFrame` + `IsHandlerFrame` predicates as
 * `parseStackLocation` and `formatStackFrames`, so which frames count as "meaningful" is decided in
 * exactly one place and the three views can never disagree.
 *
 * `method` falls back to the file name rather than "" — an empty method makes `problemId` read
 * "Error at " and collapses unrelated failures into one group.
 *
 * Never throws — like its two siblings it is called from inside the error handler: pure, no
 * KErrorMode guard, empty array on any malformed stack.
 */
export const parseStackFrames = (stack: string | undefined | null): IStackFrame[] => {
    const Frames: IStackFrame[] = [];
    if (stack === undefined || stack === null || stack === "") { return Frames; }
    try {
        for (const Line of String(stack).split("\n")) {
            const Where: IStackLocation | null = ParseStackFrame(Line);
            if (Where === null) { continue; }
            if (IsHandlerFrame(Where.FileName) === true) { continue; }
            let Method: string = StackMethodName(Line);
            if (Method === "") { Method = Where.FileName; }
            Frames.push({
                level: Frames.length,
                method: Method,
                fileName: Where.FileName,
                line: Where.LineNumber,
            });
        }
    } catch {
        return Frames;
    }
    return Frames;
};

// ── Pure utility functions ────────────────────────────────────────────────────
// nz / nb / n0 / kNotNullOrUndefined / kNotEmptyString / roundToTwo live in the leaf kCoreHandlerValues.ts since
// 2026-10-22 (so kCoreHandlerText / kCoreHandlerUtil need not import this file); re-exported here unchanged.
export { kNotNullOrUndefined, nz, nb, n0, kNotEmptyString, roundToTwo } from "./kCoreHandlerValues";

// ── Strict guards over UNTRUSTED JSON (REST / Graph responses) ─────────────────────────────────
//
// ⚠ THESE ARE NOT `nz` / `n0` UNDER A NEW NAME, AND SWAPPING ONE FOR THE OTHER IS A REGRESSION.
// `nz`/`n0` above are LENIENT COERCERS for values whose shape you already know: `nz({a:1})` hands the
// OBJECT straight back while its signature promises a `string`, and `n0("1 234")` strips spaces to
// parse thousand separators. Both are right for a form field and wrong for a payload a server sent
// you, where "this property is not the type I expected" is the case that must not slip through typed
// as though it were.
//
// These three are TYPE GUARDS over `unknown`: they answer the declared type or the fallback, never a
// lie. Consumers had written them 36 times over — 17 `Str`, 19 `Num` — and the copies had already
// diverged four ways, including one that returns -1 rather than 0 because on that call site a
// coerced 0 would read as "the file declares itself empty" and defeat a truncation check. That case
// is why `Fallback` is a PARAMETER: a differing default must be an argument, not a fork.

/** The string value of `Value`, or `Fallback` when it is not a string. A number is rendered; every
 *  other type (object, array, boolean, null, undefined) answers the fallback rather than a cast. */
export const StrOf = (Value: unknown, Fallback: string = ""): string => {
    if (typeof Value === "string") { return Value; }
    if (typeof Value === "number" && isNaN(Value) === false) { return String(Value); }
    return Fallback;
};

/** Strict string reader: unlike StrOf, numbers are not rendered. DOCUMENTED EXCEPTION: pure type check. */
export const StringOnlyOf = (Value: unknown, Fallback: string = ""): string => {
    if (typeof Value === "string") { return Value; }
    return Fallback;
};

/** Array or an empty array; preserves the original array identity. DOCUMENTED EXCEPTION: pure type check. */
export const ArrOf = (Value: unknown): unknown[] => {
    if (Array.isArray(Value)) { return Value; }
    return [];
};

/**
 * The numeric value of `Value`, or `Fallback` when it is not a number.
 *
 * ⚠ NaN NEVER ESCAPES. A numeric string is parsed, but an unparseable one answers the fallback — the
 * guard the dominant hand-rolled variant lacked. It matters most where the result is compared for
 * equality: `NaN === Expected` is false, so a missing guard fails SAFE there by luck rather than by
 * design, and luck is not a property you can rely on at the next call site.
 */
export const NumOf = (Value: unknown, Fallback: number = 0): number => {
    if (typeof Value === "number") {
        if (isNaN(Value)) { return Fallback; }
        return Value;
    }
    if (typeof Value === "string" && Value.trim() !== "") {
        const Parsed: number = Number(Value);
        if (isNaN(Parsed) === false) { return Parsed; }
    }
    return Fallback;
};

/** `Value` as a property bag, or an EMPTY one when it is not an object. Arrays and null answer empty:
 *  both are `typeof "object"`, and treating either as a record is how a `.Length` read silently
 *  becomes undefined instead of failing. */
export const ObjOf = (Value: unknown): Record<string, unknown> => {
    if (Value === null || Value === undefined) { return {}; }
    if (typeof Value !== "object") { return {}; }
    if (Array.isArray(Value)) { return {}; }
    return Value as Record<string, unknown>;
};

/**
 * The human-readable message carried by a caught `unknown`, or `Fallback` when it carries none.
 *
 * The fourth type guard of the set above, and it exists for the same reason: a catch block or an error
 * callback receives `unknown`, and the ONLY thing worth keeping from it is usually the message. Written
 * out by hand it is four lines every time, so in practice it is not written at all — the value is
 * dropped and replaced with a constant. Measured on MOSE 2026-08-19: `MoseImagePdfRenderHandler.OnError`
 * and its `MoseTextPdf` twin both took the real pdfMake error and answered the fixed string
 * `"pdf render failed"`, so a live host refusing six records a cycle could say only THAT, six times.
 *
 * ⚠ It answers the MESSAGE, never the stack — a message is for a caller to pass on in a log line or a
 * refusal reason. Anything that should be REPORTED goes to KGlobalErrorHandler, which captures the stack
 * and the version; this is not a substitute for that, and using it instead of reporting is the hidden
 * third fate.
 */
export const MessageOf = (Value: unknown, Fallback: string = ""): string => {
    if (Value instanceof Error) {
        if (Value.message !== "") { return Value.message; }
        return Fallback;
    }
    if (typeof Value === "string" && Value !== "") { return Value; }
    // A rejected fetch / SDK object is commonly a plain object with a message, not an Error instance.
    if (Value !== null && Value !== undefined && typeof Value === "object") {
        const Bag: Record<string, unknown> = Value as Record<string, unknown>;
        if (typeof Bag.message === "string" && Bag.message !== "") { return Bag.message; }
    }
    return Fallback;
};

/**
 * The error to answer a failed call with: `Err` itself when there is one, else a new Error("could not <What>") - for a
 * callback that says "failed" with no error object (an `Ok: false` answer). DOCUMENTED EXCEPTION: pure.
 * Lifted from KDHTableLoad.ErrorOf / KDHHistoryImporter.ErrorOf (kDevHost, reuse #15160).
 */
export const ErrorOf = (Err: unknown, What: string): unknown => {
    if (Err !== null && Err !== undefined) { return Err; }
    return new Error("could not " + What);
};

/**
 * kJsonLogLine - ONE structured log line on the console (App Service / journald pick it up):
 * {<Kind>: What, at: <ISO UTC>, ...Data}. `Kind` is the product's key ("kdh", "kda"), so one grep finds a product's
 * lines. Lifted from KDHQueue.Log / KDADrain.Log / KDATurn.Log (kDevHost / kDevAgent, reuse #15157).
 */
export class kJsonLogLine {
    public static Write = (Kind: string, What: string, Data: Record<string, unknown> = {}): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Head: Record<string, unknown> = {};
            Head[Kind] = What;
            Head["at"] = new Date().toISOString();
            console.log(JSON.stringify(Object.assign(Head, Data)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kJsonLogLine.Write");
        }
    };
}

/** Cryptographically-weak random integer in [min, max] (inclusive). */
export const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

/** Invoke cb as a function if it is one, passing the remaining args. */
export const doCallBack = (cb: any, ...args: any[]): void => {
    if (typeof cb === "function") (cb as (...a: any[]) => void)(...args);
};

/**
 * Returns the system locale tag (e.g. "en-US").
 * Uses Intl.DateTimeFormat — available in Node.js ≥ 13 and all modern browsers.
 */
export const serverLocale = (): string => {
    try { return Intl.DateTimeFormat().resolvedOptions().locale; }
    catch { return "en-US"; }
};

// ── kRegionalFormat ─────────────────────────────────────────────────────────
/** Regional number/date formatting record (one row of kCore._localeDateFormat). */
export class kRegionalFormat {
    /** @description Date format */
    public Df: string = "";
    /** @description Decimal separator */
    public Dec = "";
    /** @description Thousand separator */
    public Ts: string = "";
    /** @description First day of week. 0 = Sunday, 1 = Monday */
    public FDW: number = 0;
    /** @description 12-hour clock */
    public T12: boolean = false;
    /** @description BCP-47 culture tag */
    public BC47: string = "";
    /** @description Currency code */
    public Curr: string = "";
}

// ── kCore ─────────────────────────────────────────────────────────────────────
/**
 * kCore — server-safe base class holding the browser-agnostic static helpers that
 * used to live on the browser `ks` class (kalmstromShared4). The browser class now
 * does `export default class ks extends kCore`, so every existing `ks.Nz(...)`,
 * `ks.GetInitials(...)` etc. call keeps working via static inheritance, while the
 * pure logic is reusable from Node.js / Azure Functions through `kCore` directly.
 *
 * Error handling goes through the module singleton `getCoreUtils()` (registered to
 * `ks` in the browser via SetCoreUtils, defaulting to console-only on the server) —
 * NOT through `this`, because static arrow properties capture the base class, not
 * the subclass. The basic helpers delegate to the richer free functions above.
 *
 * No browser APIs. Safe to import anywhere.
 */
export class kCore {
    // ── Colour palettes ───────────────────────────────────────────────────────
    public static StandardColors: string[] = ["255,255,128","0,128,255","192,192,192","25,216,206","128,128,255","255,128,255","128,128,64","242,198,99","128,0,255","255,128,128","37,251,160","9,28,195","108,202,68","104,104,104","255,0,0","0,255,0","0,0,255","255,165,0","255,20,147","75,0,130","255,215,0","0,206,209","50,205,50","220,20,60","244,164,96","123,104,238","127,255,212","210,105,30","255,69,0","34,139,34","255,99,71","0,191,255","186,85,211","255,140,0","46,139,87","147,112,219","60,179,113","205,92,92","218,165,32","199,21,133","135,206,235","72,61,139","176,196,222","244,164,176","139,0,0","0,100,0","25,25,112","240,230,140","255,182,193","112,128,144"];
    public static ModernChartColors: string[] = ["54,162,235","255,99,132","75,192,192","255,206,86","153,102,255","255,159,64","46,204,113","231,76,60","52,152,219","155,89,182","241,196,15","26,188,156","230,126,34","149,165,166","52,73,94","236,240,241"];

    // ── Error reporting to the Power Automate service ─────────────────────────
    // CORE so BOTH hosts inherit one implementation: the browser `ks extends
    // kCore` (so `ks.KReportError` resolves here) AND the Azure Function's
    // FunctionErrorHandler calls `kCore.KReportError` from its KGlobalErrorHandler.
    // Uses `fetch` (browser + Node 18+) — a direct HTTP POST, NOT an SP REST call,
    // so it reports WITH KErrorMode set (the designed short-circuit never blocks
    // it). The error handler calls this at the moment of the error.
    //
    // THE ADDRESS IS THE ERROR RELAY (kErrorRelay, 2026-10-08, owner decision), not the Power
    // Automate flow itself. It used to be the flow's trigger URL, which carries its `sig=` key, so
    // every bundle that imported this file shipped that key. The relay's address carries no key:
    // it checks the report against the ten-field schema, removes user names in paths, credentials
    // and e-mail addresses, rate-limits per sender (100 an hour) and in total (500 a day),
    // forwards the same error at most once an hour, and passes it on to the flow, whose own
    // address lives only in the relay's Key Vault. Source: X:\Source\kShared\azure\kErrorRelay.
    //
    // THE RELAY REFUSES WHAT THE FLOW USED TO SWALLOW: an unknown field, a wrong type or an
    // over-long value is answered 400 and NOT forwarded. KFitReport below is therefore part of
    // the contract, not tidying - it is what keeps a report from being refused for its shape.
    public static KServicesURL: string = "https://kerrorrelay-pstt65n2.azurewebsites.net/api/report";

    // The relay's schema: field name -> maximum length. ErrorNumber is the one number.
    private static kReportLimits: { [Field: string]: number } = {
        ProductName: 200, ProductVersion: 50, FunctionName: 300, ErrorFileName: 260, ErrorDescription: 2000,
        // 9000, not the relay's 12000: the whole report must also stay under its 16 KB, and a stack
        // with non-ASCII text is up to three bytes a character.
        Comments: 9000, ClientName: 200, ClientEmail: 200, OSVersion: 200,
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerReport.KFitReport
    public static KFitReport = (Data: any): { [Field: string]: string | number } => { return kCoreHandlerReport.KFitReport(this, Data); };

    // Below the relay's 16384, with room for the JSON's own escaping of quotes and line breaks.
    private static kReportMaxBytes: number = 15000;

    /** DOCUMENTED EXCEPTION: pure measurement on the reporting path (see KFitReport). The size of
        the report as the relay will count it: UTF-8 bytes of its JSON. */
    private static kReportBytes = (Fit: { [Field: string]: string | number }): number => {
        return new TextEncoder().encode(JSON.stringify(Fit)).length;
    };

    // De-dup: the same error (by description) reports at most once per 5 min, so a
    // recurring fault (e.g. a 2-second timer hitting a missing list) raises ONE
    // notification, not a flood. Module-level, so it spans the func host's ticks.
    private static kLastErrorSignature: string = "";
    private static kLastErrorReportedAt: number = 0;

    /** Reports KReportError did NOT post because KReportsAreSuppressed() was true (newest last).
        Tests read this to see that the handler fired without a report reaching the live relay. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static KReportsSuppressed: any[] = [];

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerReport.KReportsAreSuppressed
    public static KReportsAreSuppressed = (): boolean => { return kCoreHandlerReport.KReportsAreSuppressed(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerReport.SuppressReport
    private static kSuppressReport = (DataToSend: any): boolean => { return kCoreHandlerReport.SuppressReport(this, DataToSend); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerReport.KReportError
    public static KReportError = (DataToSend: any): void => { return kCoreHandlerReport.KReportError(this, DataToSend); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerReport.ShouldReportError
    private static kShouldReportError = (data: any): boolean => { return kCoreHandlerReport.ShouldReportError(this, data); };
    // Named settle handler (both fulfil + reject) so the .then carries no inline
    // callback. The report is best-effort; the outcome is intentionally ignored.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static KReportErrorSettled = (_result: any): void => { return; };

    // ── Host / app context (data only; the browser ks populates these at startup) ─
    public static EnumAppType: any = {
        HTML: 1,
        Sandbox: 2,
        SPAddin: 3,
        SPAddinPart: 4,
        SPFX: 5,
        SPTeams: 6,
    };
    public static KApplication: any = {
        IsSharepointVersion: false,
        version: "",
        name: "",
        shortname: "",
        LCID: 0, //used when the application should NOT detect language from the regional settings
        apptype: kCore.EnumAppType.Sandbox, //enum key is Sandbox (capital S); lowercase "sandbox" resolved to undefined
    };
    // Server-safe product identity (no ks, no window): the generated <X>Shared.Init of a product that also runs
    // in Node / an Azure Function calls this, and the browser ks.InitProduct delegates here.
    public static InitProduct = (Name: string, ShortName: string, Version: string, AppType: number = kCore.EnumAppType.Sandbox): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            kCore.KApplication.name = Name;
            kCore.KApplication.shortname = ShortName;
            kCore.KApplication.version = Version;
            kCore.KApplication.apptype = AppType;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kCore.InitProduct");
        }
    };
    public static UserObject: any = {
        ID: "",
        Name: "",
        Email: "",
        LoginName: "",
        IsSiteAdmin: false,
        PictureUrl: "",
        JobTitle: "",
        Phone: "",
    };
    public static ErrorDetails: any = {
        FunctionName: "",
        Message: "",
        LineNumber: 0,
        ErrorFile: "",
    };
    public static LocalStorageLimit = 100000;
    public static ExcelServerURL = "";
    public static Extraparameters = "";

    // ── Regional formatting ─────────────────────────────────────────────────────
    public static NumberandDateFormat: kRegionalFormat | null = null;
    // The table itself lives in kCoreLocaleTableCore.ts (2026-10-19 split): the SAME array object, so kCore._localeDateFormat
    // reads and writes exactly as before.
    public static _localeDateFormat: any[] = kCoreLocaleDateFormat;

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerLocale.GetFullCountryAndRegionFromCountry
    public static GetFullCountryAndRegionFromCountry = (TwoLetterCountryCode: string): string => {
        return kCoreHandlerLocale.GetFullCountryAndRegionFromCountry(this, TwoLetterCountryCode);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerLocale.GetLocalDataFormat
    public static GetLocalDataFormat = (CountryCode: string): void => { return kCoreHandlerLocale.GetLocalDataFormat(this, CountryCode); };

    // ── Null / type guards (delegate to the richer free functions above) ───────
    public static KNotNullOrUndefined = (varToCheck: any): boolean => kNotNullOrUndefined(varToCheck);

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.Nz
    public static Nz = (value: any): string => { return kCoreHandlerText.Nz(this, value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.Nb
    public static Nb = (value: any): boolean => { return kCoreHandlerText.Nb(this, value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.N0
    public static N0 = (value: any): number => { return kCoreHandlerText.N0(this, value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.Nt
    public static Nt = (value: any): string => { return kCoreHandlerText.Nt(this, value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.BooleanToString
    public static BooleanToString = (value: boolean): string => { return kCoreHandlerText.BooleanToString(this, value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.KNotEmptyString
    public static KNotEmptyString = (varToCheck: any): boolean => { return kCoreHandlerText.KNotEmptyString(this, varToCheck); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.KNotInteger
    public static KNotInteger = (varToCheck: any): boolean => { return kCoreHandlerText.KNotInteger(this, varToCheck); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.ParseBool
    public static ParseBool = (str: any): boolean => { return kCoreHandlerText.ParseBool(this, str); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.ReplaceAll
    public static ReplaceAll = (StringToFind: string, ReplaceWith: string, StringToSearchIn: string): string => {
        return kCoreHandlerText.ReplaceAll(this, StringToFind, ReplaceWith, StringToSearchIn);
    };

    public static KIdSlug = (Text: string): string => {
        return Text.toLowerCase().replace(/[^a-z0-9à-ɏ]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.FoldAccents
    public static FoldAccents = (Value: string, PreserveBlank: boolean = false): string => {
        return kCoreHandlerText.FoldAccents(this, Value, PreserveBlank);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.NormalizeName
    public static NormalizeName = (Value: string): string => { return kCoreHandlerText.NormalizeName(this, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.NameTokens
    public static NameTokens = (Name: string, Email?: string): string[] => { return kCoreHandlerText.NameTokens(this, Name, Email); };

    // NOTE: the people-name MATCHERS that build on NormalizeName/NameTokens —
    // NameMatches (any-order prefix-per-token) and NameScore (relevance ranking) —
    // live on kSearchItemsCore in kSearchCore.ts, with the rest of the search engine.
    // kCore keeps only the fold + tokenize primitives above.

    // Every occurrence, not the first (fixed 2026-09-24). This used String.replace with a
    // STRING pattern, which replaces only the first match - so "Kunde's 'Vertrag'.docx" kept
    // its 2nd and 3rd apostrophes, and those end the url='...' literal of the REST
    // /files/add(url='...') call it exists to protect (HTTP 400). "#" was not handled at all:
    // in a request URL it starts the fragment, so everything after it never reaches the
    // server. Both now go; a name that was already safe is returned unchanged.
    private static _UnsafeFileChars: string[][] = [["/", " "], [":", " "], [";", " "], ["'", " "],
        ["?", ""], ["%", ""], ["$", ""], ["(", ""], [")", ""], ["#", ""]];

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.ClearUnsafeFileChars
    public static ClearUnsafeFileChars = (OrgFileName: string): string => { return kCoreHandlerText.ClearUnsafeFileChars(this, OrgFileName); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.UnescapeJsString
    public static UnescapeJsString = (Value: string): string => { return kCoreHandlerText.UnescapeJsString(this, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.UnescapeJsStringReplacer
    public static UnescapeJsStringReplacer = (_Match: string, U: string, X: string, S: string): string => {
        return kCoreHandlerText.UnescapeJsStringReplacer(this, _Match, U, X, S);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.TruncateText
    public static TruncateText = (sourceString: string, length: number): string => {
        return kCoreHandlerText.TruncateText(this, sourceString, length);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.EncodeSpecialCharacter
    public static EncodeSpecialCharacter = (stringval: string): string => { return kCoreHandlerText.EncodeSpecialCharacter(this, stringval); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.DecodeSpecialCharacter
    public static DecodeSpecialCharacter = (stringval: string): string => { return kCoreHandlerText.DecodeSpecialCharacter(this, stringval); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.RemoveEndSlash
    public static RemoveEndSlash = (str: string): string => { return kCoreHandlerText.RemoveEndSlash(this, str); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.InitialsOfName
    public static InitialsOfName = (Name: string): string => { return kCoreHandlerText.InitialsOfName(this, Name); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.GetInitials
    public static GetInitials = (str: string, initialCount = 2): string => { return kCoreHandlerText.GetInitials(this, str, initialCount); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.ValidateEmail
    public static ValidateEmail = (email: string): boolean => { return kCoreHandlerText.ValidateEmail(this, email); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.EncodeUrl
    public static EncodeUrl = (url: string): string => { return kCoreHandlerText.EncodeUrl(this, url); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.AppendQueryString
    public static AppendQueryString = (url: string, paramName: string, paramValue: string): string => {
        return kCoreHandlerText.AppendQueryString(this, url, paramName, paramValue);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.RemoveURLParameter
    public static RemoveURLParameter = (url: string, paramName: string): string => {
        return kCoreHandlerText.RemoveURLParameter(this, url, paramName);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.ArrayMove
    public static ArrayMove = (arr: any[], old_index: number, new_index: number): any => {
        return kCoreHandlerText.ArrayMove(this, arr, old_index, new_index);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.Equals
    public static Equals = (a: any, b: any): boolean => { return kCoreHandlerText.Equals(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText._EqualsKeyFactory
    private static _EqualsKeyFactory = (a: any, b: any): ((k: string) => boolean) => { return kCoreHandlerText._EqualsKeyFactory(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.GetObjectByTitle
    public static GetObjectByTitle = (ObjectTitle: string, ObjectArray: any): any => {
        return kCoreHandlerText.GetObjectByTitle(this, ObjectTitle, ObjectArray);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.GetIndexFromID
    public static GetIndexFromID = (ID: number, ObjectArray: any[]): number => { return kCoreHandlerText.GetIndexFromID(this, ID, ObjectArray); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.ObjectNamesArray
    public static ObjectNamesArray = (ObjectArray: any[], OnlyNonHidden?: boolean): string[] => {
        return kCoreHandlerText.ObjectNamesArray(this, ObjectArray, OnlyNonHidden);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerText.ValidateValues
    public static ValidateValues = (cellValue: any, DefaultValue: any, ValueType: string): any => {
        return kCoreHandlerText.ValidateValues(this, cellValue, DefaultValue, ValueType);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.NaturalCompare
    public static NaturalCompare = (a: string, b: string): number => { return kCoreHandlerUtil.NaturalCompare(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SortObjectsByName
    public static SortObjectsByName = (a: any, b: any): number => { return kCoreHandlerUtil.SortObjectsByName(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SortObjectsByNumber
    public static SortObjectsByNumber = (a: any, b: any): number => { return kCoreHandlerUtil.SortObjectsByNumber(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SortObjectsByTitle
    public static SortObjectsByTitle = (a: any, b: any): number => { return kCoreHandlerUtil.SortObjectsByTitle(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SortObjectsBylabel
    public static SortObjectsBylabel = (a: any, b: any): number => { return kCoreHandlerUtil.SortObjectsBylabel(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SortDescObjectByID
    public static SortDescObjectByID = (a: any, b: any): number => { return kCoreHandlerUtil.SortDescObjectByID(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SortAscObjectByID
    public static SortAscObjectByID = (a: any, b: any): number => { return kCoreHandlerUtil.SortAscObjectByID(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SortObjectById
    public static SortObjectById = (a: any, b: any): number => { return kCoreHandlerUtil.SortObjectById(this, a, b); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.RoundToTwo
    public static RoundToTwo = (num: number): number => { return kCoreHandlerUtil.RoundToTwo(this, num); };

    public static GetRandomInt = (min: number, max: number): number => getRandomInt(min, max);

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.RandomHex
    public static RandomHex = (ByteCount: number, SecureOnly: boolean = false): string => {
        return kCoreHandlerUtil.RandomHex(this, ByteCount, SecureOnly);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.NewGuid
    public static NewGuid = (): string => { return kCoreHandlerUtil.NewGuid(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil._NativeGuid
    private static _NativeGuid = (): string => { return kCoreHandlerUtil._NativeGuid(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil._NewGuidReplacer
    private static _NewGuidReplacer = (c: string): string => { return kCoreHandlerUtil._NewGuidReplacer(this, c); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.AddOffSet
    public static AddOffSet = (Currdate: Date): Date | null => { return kCoreHandlerUtil.AddOffSet(this, Currdate); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.ColorLog
    public static ColorLog = (message: string, color: string): void => { return kCoreHandlerUtil.ColorLog(this, message, color); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.ThrowError
    public static ThrowError = (): number => { return kCoreHandlerUtil.ThrowError(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.GetNextColor
    public static GetNextColor = (ArrayWithColors: any[]): string => { return kCoreHandlerUtil.GetNextColor(this, ArrayWithColors); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.GetNextHexColor
    public static GetNextHexColor = (ArrayWithColors: any[]): string => { return kCoreHandlerUtil.GetNextHexColor(this, ArrayWithColors); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.GetRGBCodeFromHex
    public static GetRGBCodeFromHex = (HexColorCode: string): string => { return kCoreHandlerUtil.GetRGBCodeFromHex(this, HexColorCode); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.GetRGB
    public static GetRGB = (color: string): string => { return kCoreHandlerUtil.GetRGB(this, color); };

    public static HexToR = (h: string): number => parseInt(kCore.CutHex(h).substring(0, 2), 16);
    public static HexToG = (h: string): number => parseInt(kCore.CutHex(h).substring(2, 4), 16);
    public static HexToB = (h: string): number => parseInt(kCore.CutHex(h).substring(4, 6), 16);

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.CutHex
    public static CutHex = (h: string): string => { return kCoreHandlerUtil.CutHex(this, h); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.GetRawColor
    public static GetRawColor = (R: number, G: number, B: number): any => { return kCoreHandlerUtil.GetRawColor(this, R, G, B); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreHandlerUtil.SetCaptionColorStyle
    public static SetCaptionColorStyle = (ColorCode: string, Important?: boolean): string => {
        return kCoreHandlerUtil.SetCaptionColorStyle(this, ColorCode, Important);
    };

    // Top-level domains used by validateEmail (moved verbatim from ks.ValidateEmail).
    private static _ValidEmailTlds: string[] = ['aaa', 'aarp', 'abb', 'abbott', 'abbvie', 'abc', 'able', 'abogado', 'abudhabi', 'ac', 'academy', 'accenture', 'accountant', 'accountants', 'aco', 'actor', 'ad', 'ads', 'adult', 'ae', 'aeg', 'aero', 'aetna', 'af', 'afl', 'africa', 'ag', 'agakhan', 'agency', 'ai', 'aig', 'airbus', 'airforce', 'airtel', 'akdn', 'al', 'alibaba', 'alipay', 'allfinanz', 'allstate', 'ally', 'alsace', 'alstom', 'am', 'amazon', 'americanexpress', 'americanfamily', 'amex', 'amfam', 'amica', 'amsterdam', 'analytics', 'android', 'anquan', 'anz', 'ao', 'aol', 'apartments', 'app', 'apple', 'aq', 'aquarelle', 'ar', 'arab', 'aramco', 'archi', 'army', 'arpa', 'art', 'arte', 'as', 'asda', 'asia', 'associates', 'at', 'athleta', 'attorney', 'au', 'auction', 'audi', 'audible', 'audio', 'auspost', 'author', 'auto', 'autos', 'aw', 'aws', 'ax', 'axa', 'az', 'azure', 'ba', 'baby', 'baidu', 'banamex', 'band', 'bank', 'bar', 'barcelona', 'barclaycard', 'barclays', 'barefoot', 'bargains', 'baseball', 'basketball', 'bauhaus', 'bayern', 'bb', 'bbc', 'bbt', 'bbva', 'bcg', 'bcn', 'bd', 'be', 'beats', 'beauty', 'beer', 'bentley', 'berlin', 'best', 'bestbuy', 'bet', 'bf', 'bg', 'bh', 'bharti', 'bi', 'bible', 'bid', 'bike', 'bing', 'bingo', 'bio', 'biz', 'bj', 'black', 'blackfriday', 'blockbuster', 'blog', 'bloomberg', 'blue', 'bm', 'bms', 'bmw', 'bn', 'bnpparibas', 'bo', 'boats', 'boehringer', 'bofa', 'bom', 'bond', 'boo', 'book', 'booking', 'bosch', 'bostik', 'boston', 'bot', 'boutique', 'box', 'br', 'bradesco', 'bridgestone', 'broadway', 'broker', 'brother', 'brussels', 'bs', 'bt', 'build', 'builders', 'business', 'buy', 'buzz', 'bv', 'bw', 'by', 'bz', 'bzh', 'ca', 'cab', 'cafe', 'cal', 'call', 'calvinklein', 'cam', 'camera', 'camp', 'canon', 'capetown', 'capital', 'capitalone', 'car', 'caravan', 'cards', 'care', 'career', 'careers', 'cars', 'casa', 'case', 'cash', 'casino', 'cat', 'catering', 'catholic', 'cba', 'cbn', 'cbre', 'cc', 'cd', 'center', 'ceo', 'cern', 'cf', 'cfa', 'cfd', 'cg', 'ch', 'chanel', 'channel', 'charity', 'chase', 'chat', 'cheap', 'chintai', 'christmas', 'chrome', 'church', 'ci', 'cipriani', 'circle', 'cisco', 'citadel', 'citi', 'citic', 'city', 'ck', 'cl', 'claims', 'cleaning', 'click', 'clinic', 'clinique', 'clothing', 'cloud', 'club', 'clubmed', 'cm', 'cn', 'co', 'coach', 'codes', 'coffee', 'college', 'cologne', 'com', 'commbank', 'community', 'company', 'compare', 'computer', 'comsec', 'condos', 'construction', 'consulting', 'contact', 'contractors', 'cooking', 'cool', 'coop', 'corsica', 'country', 'coupon', 'coupons', 'courses', 'cpa', 'cr', 'credit', 'creditcard', 'creditunion', 'cricket', 'crown', 'crs', 'cruise', 'cruises', 'cu', 'cuisinella', 'cv', 'cw', 'cx', 'cy', 'cymru', 'cyou', 'cz', 'dad', 'dance', 'data', 'date', 'dating', 'datsun', 'day', 'dclk', 'dds', 'de', 'deal', 'dealer', 'deals', 'degree', 'delivery', 'dell', 'deloitte', 'delta', 'democrat', 'dental', 'dentist', 'desi', 'design', 'dev', 'dhl', 'diamonds', 'diet', 'digital', 'direct', 'directory', 'discount', 'discover', 'dish', 'diy', 'dj', 'dk', 'dm', 'dnp', 'do', 'docs', 'doctor', 'dog', 'domains', 'dot', 'download', 'drive', 'dtv', 'dubai', 'dunlop', 'dupont', 'durban', 'dvag', 'dvr', 'dz', 'earth', 'eat', 'ec', 'eco', 'edeka', 'edu', 'education', 'ee', 'eg', 'email', 'emerck', 'energy', 'engineer', 'engineering', 'enterprises', 'epson', 'equipment', 'er', 'ericsson', 'erni', 'es', 'esq', 'estate', 'et', 'eu', 'eurovision', 'eus', 'events', 'exchange', 'expert', 'exposed', 'express', 'extraspace', 'fage', 'fail', 'fairwinds', 'faith', 'family', 'fan', 'fans', 'farm', 'farmers', 'fashion', 'fast', 'fedex', 'feedback', 'ferrari', 'ferrero', 'fi', 'fidelity', 'fido', 'film', 'final', 'finance', 'financial', 'fire', 'firestone', 'firmdale', 'fish', 'fishing', 'fit', 'fitness', 'fj', 'fk', 'flickr', 'flights', 'flir', 'florist', 'flowers', 'fly', 'fm', 'fo', 'foo', 'food', 'football', 'ford', 'forex', 'forsale', 'forum', 'foundation', 'fox', 'fr', 'free', 'fresenius', 'frl', 'frogans', 'frontier', 'ftr', 'fujitsu', 'fun', 'fund', 'furniture', 'futbol', 'fyi', 'ga', 'gal', 'gallery', 'gallo', 'gallup', 'game', 'games', 'gap', 'garden', 'gay', 'gb', 'gbiz', 'gd', 'gdn', 'ge', 'gea', 'gent', 'genting', 'george', 'gf', 'gg', 'ggee', 'gh', 'gi', 'gift', 'gifts', 'gives', 'giving', 'gl', 'glass', 'gle', 'global', 'globo', 'gm', 'gmail', 'gmbh', 'gmo', 'gmx', 'gn', 'godaddy', 'gold', 'goldpoint', 'golf', 'goo', 'goodyear', 'goog', 'google', 'gop', 'got', 'gov', 'gp', 'gq', 'gr', 'grainger', 'graphics', 'gratis', 'green', 'gripe', 'grocery', 'group', 'gs', 'gt', 'gu', 'gucci', 'guge', 'guide', 'guitars', 'guru', 'gw', 'gy', 'hair', 'hamburg', 'hangout', 'haus', 'hbo', 'hdfc', 'hdfcbank', 'health', 'healthcare', 'help', 'helsinki', 'here', 'hermes', 'hiphop', 'hisamitsu', 'hitachi', 'hiv', 'hk', 'hkt', 'hm', 'hn', 'hockey', 'holdings', 'holiday', 'homedepot', 'homegoods', 'homes', 'homesense', 'honda', 'horse', 'hospital', 'host', 'hosting', 'hot', 'hotels', 'hotmail', 'house', 'how', 'hr', 'hsbc', 'ht', 'hu', 'hughes', 'hyatt', 'hyundai', 'ibm', 'icbc', 'ice', 'icu', 'id', 'ie', 'ieee', 'ifm', 'ikano', 'il', 'im', 'imamat', 'imdb', 'immo', 'immobilien', 'in', 'inc', 'industries', 'infiniti', 'info', 'ing', 'ink', 'institute', 'insurance', 'insure', 'int', 'international', 'intuit', 'investments', 'io', 'ipiranga', 'iq', 'ir', 'irish', 'is', 'ismaili', 'ist', 'istanbul', 'it', 'itau', 'itv', 'jaguar', 'java', 'jcb', 'je', 'jeep', 'jetzt', 'jewelry', 'jio', 'jll', 'jm', 'jmp', 'jnj', 'jo', 'jobs', 'joburg', 'jot', 'joy', 'jp', 'jpmorgan', 'jprs', 'juegos', 'juniper', 'kaufen', 'kddi', 'ke', 'kerryhotels', 'kerrylogistics', 'kerryproperties', 'kfh', 'kg', 'kh', 'ki', 'kia', 'kids', 'kim', 'kindle', 'kitchen', 'kiwi', 'km', 'kn', 'koeln', 'komatsu', 'kosher', 'kp', 'kpmg', 'kpn', 'kr', 'krd', 'kred', 'kuokgroup', 'kw', 'ky', 'kyoto', 'kz', 'la', 'lacaixa', 'lamborghini', 'lamer', 'lancaster', 'land', 'landrover', 'lanxess', 'lasalle', 'lat', 'latino', 'latrobe', 'law', 'lawyer', 'lb', 'lc', 'lds', 'lease', 'leclerc', 'lefrak', 'legal', 'lego', 'lexus', 'lgbt', 'li', 'lidl', 'life', 'lifeinsurance', 'lifestyle', 'lighting', 'like', 'lilly', 'limited', 'limo', 'lincoln', 'link', 'lipsy', 'live', 'living', 'lk', 'llc', 'llp', 'loan', 'loans', 'locker', 'locus', 'lol', 'london', 'lotte', 'lotto', 'love', 'lpl', 'lplfinancial', 'lr', 'ls', 'lt', 'ltd', 'ltda', 'lu', 'lundbeck', 'luxe', 'luxury', 'lv', 'ly', 'ma', 'madrid', 'maif', 'maison', 'makeup', 'man', 'management', 'mango', 'map', 'market', 'marketing', 'markets', 'marriott', 'marshalls', 'mattel', 'mba', 'mc', 'mckinsey', 'md', 'me', 'med', 'media', 'meet', 'melbourne', 'meme', 'memorial', 'men', 'menu', 'merckmsd', 'mg', 'mh', 'miami', 'microsoft', 'mil', 'mini', 'mint', 'mit', 'mitsubishi', 'mk', 'ml', 'mlb', 'mls', 'mm', 'mma', 'mn', 'mo', 'mobi', 'mobile', 'moda', 'moe', 'moi', 'mom', 'monash', 'money', 'monster', 'mormon', 'mortgage', 'moscow', 'moto', 'motorcycles', 'mov', 'movie', 'mp', 'mq', 'mr', 'ms', 'msd', 'mt', 'mtn', 'mtr', 'mu', 'museum', 'music', 'mv', 'mw', 'mx', 'my', 'mz', 'na', 'nab', 'nagoya', 'name', 'navy', 'nba', 'nc', 'ne', 'nec', 'net', 'netbank', 'netflix', 'network', 'neustar', 'new', 'news', 'next', 'nextdirect', 'nexus', 'nf', 'nfl', 'ng', 'ngo', 'nhk', 'ni', 'nico', 'nike', 'nikon', 'ninja', 'nissan', 'nissay', 'nl', 'no', 'nokia', 'norton', 'now', 'nowruz', 'nowtv', 'np', 'nr', 'nra', 'nrw', 'ntt', 'nu', 'nyc', 'nz', 'obi', 'observer', 'office', 'okinawa', 'olayan', 'olayangroup', 'ollo', 'om', 'omega', 'one', 'ong', 'onl', 'online', 'ooo', 'open', 'oracle', 'orange', 'org', 'organic', 'origins', 'osaka', 'otsuka', 'ott', 'ovh', 'pa', 'page', 'panasonic', 'paris', 'pars', 'partners', 'parts', 'party', 'pay', 'pccw', 'pe', 'pet', 'pf', 'pfizer', 'pg', 'ph', 'pharmacy', 'phd', 'philips', 'phone', 'photo', 'photography', 'photos', 'physio', 'pics', 'pictet', 'pictures', 'pid', 'pin', 'ping', 'pink', 'pioneer', 'pizza', 'pk', 'pl', 'place', 'play', 'playstation', 'plumbing', 'plus', 'pm', 'pn', 'pnc', 'pohl', 'poker', 'politie', 'porn', 'post', 'pr', 'pramerica', 'praxi', 'press', 'prime', 'pro', 'prod', 'productions', 'prof', 'progressive', 'promo', 'properties', 'property', 'protection', 'pru', 'prudential', 'ps', 'pt', 'pub', 'pw', 'pwc', 'py', 'qa', 'qpon', 'quebec', 'quest', 'racing', 'radio', 're', 'read', 'realestate', 'realtor', 'realty', 'recipes', 'red', 'redstone', 'redumbrella', 'rehab', 'reise', 'reisen', 'reit', 'reliance', 'ren', 'rent', 'rentals', 'repair', 'report', 'republican', 'rest', 'restaurant', 'review', 'reviews', 'rexroth', 'rich', 'richardli', 'ricoh', 'ril', 'rio', 'rip', 'ro', 'rocks', 'rodeo', 'rogers', 'room', 'rs', 'rsvp', 'ru', 'rugby', 'ruhr', 'run', 'rw', 'rwe', 'ryukyu', 'sa', 'saarland', 'safe', 'safety', 'sakura', 'sale', 'salon', 'samsclub', 'samsung', 'sandvik', 'sandvikcoromant', 'sanofi', 'sap', 'sarl', 'sas', 'save', 'saxo', 'sb', 'sbi', 'sbs', 'sc', 'scb', 'schaeffler', 'schmidt', 'scholarships', 'school', 'schule', 'schwarz', 'science', 'scot', 'sd', 'se', 'search', 'seat', 'secure', 'security', 'seek', 'select', 'sener', 'services', 'seven', 'sew', 'sex', 'sexy', 'sfr', 'sg', 'sh', 'shangrila', 'sharp', 'shell', 'shia', 'shiksha', 'shoes', 'shop', 'shopping', 'shouji', 'show', 'si', 'silk', 'sina', 'singles', 'site', 'sj', 'sk', 'ski', 'skin', 'sky', 'skype', 'sl', 'sling', 'sm', 'smart', 'smile', 'sn', 'sncf', 'so', 'soccer', 'social', 'softbank', 'software', 'sohu', 'solar', 'solutions', 'song', 'sony', 'soy', 'spa', 'space', 'sport', 'spot', 'sr', 'srl', 'ss', 'st', 'stada', 'staples', 'star', 'statebank', 'statefarm', 'stc', 'stcgroup', 'stockholm', 'storage', 'store', 'stream', 'studio', 'study', 'style', 'su', 'sucks', 'supplies', 'supply', 'support', 'surf', 'surgery', 'suzuki', 'sv', 'swatch', 'swiss', 'sx', 'sy', 'sydney', 'systems', 'sz', 'tab', 'taipei', 'talk', 'taobao', 'target', 'tatamotors', 'tatar', 'tattoo', 'tax', 'taxi', 'tc', 'tci', 'td', 'tdk', 'team', 'tech', 'technology', 'tel', 'temasek', 'tennis', 'teva', 'tf', 'tg', 'th', 'thd', 'theater', 'theatre', 'tiaa', 'tickets', 'tienda', 'tips', 'tires', 'tirol', 'tj', 'tjmaxx', 'tjx', 'tk', 'tkmaxx', 'tl', 'tm', 'tmall', 'tn', 'to', 'today', 'tokyo', 'tools', 'top', 'toray', 'toshiba', 'total', 'tours', 'town', 'toyota', 'toys', 'tr', 'trade', 'trading', 'training', 'travel', 'travelers', 'travelersinsurance', 'trust', 'trv', 'tt', 'tube', 'tui', 'tunes', 'tushu', 'tv', 'tvs', 'tw', 'tz', 'ua', 'ubank', 'ubs', 'ug', 'uk', 'unicom', 'university', 'uno', 'uol', 'ups', 'us', 'uy', 'uz', 'va', 'vacations', 'vana', 'vanguard', 'vc', 've', 'vegas', 'ventures', 'verisign', 'versicherung', 'vet', 'vg', 'vi', 'viajes', 'video', 'vig', 'viking', 'villas', 'vin', 'vip', 'virgin', 'visa', 'vision', 'viva', 'vivo', 'vlaanderen', 'vn', 'vodka', 'volvo', 'vote', 'voting', 'voto', 'voyage', 'vu', 'wales', 'walmart', 'walter', 'wang', 'wanggou', 'watch', 'watches', 'weather', 'weatherchannel', 'webcam', 'weber', 'website', 'wed', 'wedding', 'weibo', 'weir', 'wf', 'whoswho', 'wien', 'wiki', 'williamhill', 'win', 'windows', 'wine', 'winners', 'wme', 'wolterskluwer', 'woodside', 'work', 'works', 'world', 'wow', 'ws', 'wtc', 'wtf', 'xbox', 'xerox', 'xihuan', 'xin', 'xn--11b4c3d', 'xn--1ck2e1b', 'xn--1qqw23a', 'xn--2scrj9c', 'xn--30rr7y', 'xn--3bst00m', 'xn--3ds443g', 'xn--3e0b707e', 'xn--3hcrj9c', 'xn--3pxu8k', 'xn--42c2d9a', 'xn--45br5cyl', 'xn--45brj9c', 'xn--45q11c', 'xn--4dbrk0ce', 'xn--4gbrim', 'xn--54b7fta0cc', 'xn--55qw42g', 'xn--55qx5d', 'xn--5su34j936bgsg', 'xn--5tzm5g', 'xn--6frz82g', 'xn--6qq986b3xl', 'xn--80adxhks', 'xn--80ao21a', 'xn--80aqecdr1a', 'xn--80asehdb', 'xn--80aswg', 'xn--8y0a063a', 'xn--90a3ac', 'xn--90ae', 'xn--90ais', 'xn--9dbq2a', 'xn--9et52u', 'xn--9krt00a', 'xn--b4w605ferd', 'xn--bck1b9a5dre4c', 'xn--c1avg', 'xn--c2br7g', 'xn--cck2b3b', 'xn--cckwcxetd', 'xn--cg4bki', 'xn--clchc0ea0b2g2a9gcd', 'xn--czr694b', 'xn--czrs0t', 'xn--czru2d', 'xn--d1acj3b', 'xn--d1alf', 'xn--e1a4c', 'xn--eckvdtc9d', 'xn--efvy88h', 'xn--fct429k', 'xn--fhbei', 'xn--fiq228c5hs', 'xn--fiq64b', 'xn--fiqs8s', 'xn--fiqz9s', 'xn--fjq720a', 'xn--flw351e', 'xn--fpcrj9c3d', 'xn--fzc2c9e2c', 'xn--fzys8d69uvgm', 'xn--g2xx48c', 'xn--gckr3f0f', 'xn--gecrj9c', 'xn--gk3at1e', 'xn--h2breg3eve', 'xn--h2brj9c', 'xn--h2brj9c8c', 'xn--hxt814e', 'xn--i1b6b1a6a2e', 'xn--imr513n', 'xn--io0a7i', 'xn--j1aef', 'xn--j1amh', 'xn--j6w193g', 'xn--jlq480n2rg', 'xn--jvr189m', 'xn--kcrx77d1x4a', 'xn--kprw13d', 'xn--kpry57d', 'xn--kput3i', 'xn--l1acc', 'xn--lgbbat1ad8j', 'xn--mgb9awbf', 'xn--mgba3a3ejt', 'xn--mgba3a4f16a', 'xn--mgba7c0bbn0a', 'xn--mgbaam7a8h', 'xn--mgbab2bd', 'xn--mgbah1a3hjkrd', 'xn--mgbai9azgqp6j', 'xn--mgbayh7gpa', 'xn--mgbbh1a', 'xn--mgbbh1a71e', 'xn--mgbc0a9azcg', 'xn--mgbca7dzdo', 'xn--mgbcpq6gpa1a', 'xn--mgberp4a5d4ar', 'xn--mgbgu82a', 'xn--mgbi4ecexp', 'xn--mgbpl2fh', 'xn--mgbt3dhd', 'xn--mgbtx2b', 'xn--mgbx4cd0ab', 'xn--mix891f', 'xn--mk1bu44c', 'xn--mxtq1m', 'xn--ngbc5azd', 'xn--ngbe9e0a', 'xn--ngbrx', 'xn--node', 'xn--nqv7f', 'xn--nqv7fs00ema', 'xn--nyqy26a', 'xn--o3cw4h', 'xn--ogbpf8fl', 'xn--otu796d', 'xn--p1acf', 'xn--p1ai', 'xn--pgbs0dh', 'xn--pssy2u', 'xn--q7ce6a', 'xn--q9jyb4c', 'xn--qcka1pmc', 'xn--qxa6a', 'xn--qxam', 'xn--rhqv96g', 'xn--rovu88b', 'xn--rvc1e0am3e', 'xn--s9brj9c', 'xn--ses554g', 'xn--t60b56a', 'xn--tckwe', 'xn--tiq49xqyj', 'xn--unup4y', 'xn--vermgensberater-ctb', 'xn--vermgensberatung-pwb', 'xn--vhquv', 'xn--vuq861b', 'xn--w4r85el8fhu5dnra', 'xn--w4rs40l', 'xn--wgbh1c', 'xn--wgbl6a', 'xn--xhq521b', 'xn--xkc2al3hye2a', 'xn--xkc2dl3a5ee0h', 'xn--y9a3aq', 'xn--yfro4i67o', 'xn--ygbi2ammx', 'xn--zfr164b', 'xxx', 'xyz', 'yachts', 'yahoo', 'yamaxun', 'yandex', 'ye', 'yodobashi', 'yoga', 'yokohama', 'you', 'youtube', 'yt', 'yun', 'za', 'zappos', 'zara', 'zero', 'zip', 'zm', 'zone', 'zuerich', 'zw'];
}
